// src/pages/writing/loose-leaves/easter.js
import React, { useEffect } from "react";
import withBlogSEO from "../../../utils/withBlogSEO";
import FootnoteLink, {
  FootnoteProvider,
} from "../../../components/FootnoteLink";

function Easter() {
  return (
    <FootnoteProvider>
      <div className="max-w-2xl mx-auto px-4 py-8 prose prose-sm">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">easter sunday</h1>
        <div className="text-gray-600">
          <p className="italic text-sm mb-4">[4/2025]</p>

          <div className="whitespace-pre-line text-base">
            <p className="mb-4">
              It’s Easter Sunday. Yesterday evening, Archbishop and Vatican
              Ambassador to Palestine Adolfo Tito Yllana was barred from
              attending the Holy Saturday Commemoration in Jerusalem.
            </p>
            <p className="mb-4">
              I watched a video this morning of a family comforting a young girl
              as she dies. This is not a sentence I’ve invented, to describe
              with ardor the weight we feel of a crying child entrapped in Gaza.
              It was the caption: “comforting a young girl as she dies.” The
              girl had shrapnel lodged in her chest, and the medical staff
              didn’t have the supplies or machinery to remove it. She’s actively
              dying, right now, as I write this. (As I read this again, I
              realize that she may already be dead.)
            </p>
            <p className="mb-4">
              In attempt to preserve the stories from oblivion, I have planned
              to go through some of the material I’ve had saved (from news
              platforms and social media) since the start of April, walking
              backward from Easter to Eid. That is twenty days –– spanning less
              than 5% of the period since October 7, 2023.
            </p>
            <p className="mb-4">
              Writing consistently has allowed me to recognize that time has
              driven the same events out of the days, in repeated cycles. There
              is clear evidence of a crime; a Western news story downplays or
              ignores the crime; the I.D.F. circumvents responsibility;
              protestors are punished. Other aspects of life continue to
              crumble.
            </p>

            <p className="mb-2 font-bold text-black pt-2">1</p>
            <p className="mb-4">
              There was an article published in the New Yorker, called
              “Hospitals in Ruins.”
              <FootnoteLink url="https://www.newyorker.com/magazine/2025/04/28/hospitals-in-ruins" />{" "}
              Reporter Clayton Dalton wrote about his visit to one of the last
              hospitals remaining in Gaza. He noticed the healthcare machinery
              was deliberately shot.
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              Sultan led me upstairs, to the I.C.U., where wind blew through
              broken windows. He wanted to show me something that he had
              discovered after Israeli forces left the hospital. He pointed to a
              cardiac monitor near a wall. It appeared to have a bullet hole in
              its screen. Next to it was an EKG machine whose screen had been
              smashed.
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              We entered a large storage room in the corner of the I.C.U. which
              was crammed with medical devices: ultrasound machines, I.V. pumps,
              dialysis machines, blood-pressure monitors. Each had apparently
              been destroyed by a bullet –– not in a pattern one would expect
              from random shooting but, rather, methodically. I was stunned. I
              couldn't think of any possible military justification for
              destroying lifesaving equipment.
            </p>
            <p className="mb-2 font-bold text-black pt-2">2</p>
            <p className="mb-4">
              When Dalton asked the I.D.F. for comment, the spokesperson said,
              "Claims that the I.D.F. deliberately targets medical equipment are
              unequivocally false."
            </p>
            <p className="mb-2 font-bold text-black pt-2">3</p>
            <p className="mb-4">
              Just over a week ago, a story spread on the internet: “the
              paramedic murders.”
              <FootnoteLink url="https://www.theguardian.com/world/2025/apr/24/new-details-on-killing-of-paramedics-in-gaza-appear-to-contradict-idf-account" />{" "}
              "Soldiers fired continuously at the vehicles for three and a half
              minutes from close range despite the aid workers’ attempts to
              identify themselves."
            </p>
            <p className="mb-2 font-bold text-black pt-2">4</p>
            <p className="mb-4">
              In Dalton’s same article, he wrote: “Israeli forces have dropped
              more explosives in Gaza than fell on London, Dresden, and Hamburg
              combined in WW2.”
            </p>
            <p className="mb-2 font-bold text-black pt-2">5</p>
            <p className="mb-4">
              In a live interview, Netanyahu’s spokesperson Omer Dostri said:
              Hostages cannot be brought back in a single phase because Hamas is
              demanding an end to the war if so.
              <FootnoteLink url="https://www.haaretz.com/israel-news/2025-04-20/ty-article/.premium/netanyahus-spokesperson-says-its-impossible-to-return-all-hostages-in-one-stage/00000196-525a-d9e7-a3df-db7ee2f50000" />{" "}
            </p>
            <p className="mb-4">
              If the leader of Israel has specified that hostages cannot be
              released, then it must be that the war has been waged for some
              other purpose. Mehdi Hasan: “Every single pundit who said ‘if they
              released the hostages, the war would be over’ should hang their
              heads in shame.”
              <FootnoteLink url="https://x.com/mehdirhasan/status/1913848899296506150" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">6</p>
            <p className="mb-4">
              There is a video of children in Ukraine preparing for combat.
              <FootnoteLink url="https://www.facebook.com/watch/?v=645955668249705" />{" "}
              
            </p>
            <p className="mb-2 font-bold text-black pt-2">7</p>
            <p className="mb-4">
              Ahmad Abu al-Rous, a child with special needs, was killed along
              with his mother and two sisters in an air strike.
              <FootnoteLink url="https://www.instagram.com/p/DIjJYpQOJJy/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==" />{" "}
              The wheelchair to which he was consigned was found seared, covered
              in ashes.
            </p>
            <p className="mb-4">
              A video of the children’s corpses haunts me, their complete
              incineration.
              <FootnoteLink url="https://www.instagram.com/p/DIjG4X1O5up/" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">8</p>
            <p className="mb-4">
              Every couple of days, settlers invade rural areas and
              neighborhoods in the West Bank and attack Palestinians. In this
              video,
              <FootnoteLink url="https://www.instagram.com/reel/DIwAj0BC_TL/?utm_source=ig_web_copy_link" />{" "}
              the settler invaded the village called Susya and said: “Everything
              here is mine,” referring to the houses in which Palestinians live.
            </p>
            <p className="mb-4">
              I researched Susya, pulling up the Wikipedia article on the
              village. The article says that in 1986, Israel named the area an
              archaeological site, and then expelled Palestinians from their
              homes:
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              In 1983, an Israeli settlement also named Susya was established
              next to the Palestinian village. In 1986, the Israeli Defense
              Ministry's Civil Administration declared the entire area owned by
              Palestinians an archeological site, and the Israeli Defense Forces
              expelled the Palestinian owners from their dwellings and appointed
              Israeli settlers from the recently-built settlement to manage the
              site.
            </p>
            <p className="mb-4">
              It is well known that settlers are never arrested, only the
              Palestinians who resist the theft of their homes.
            </p>
            <p className="mb-2 font-bold text-black pt-2">9</p>
            <p className="mb-4">
              In a press statement by Secretary of State Marco Rubio:
              <FootnoteLink url="https://www.state.gov/protecting-and-championing-free-speech-at-the-state-department/" />
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              Freedom of speech and expression have been a cornerstone of what
              it means to be an American citizen. For centuries, the United
              States served as a beacon of hope for millions of people around
              the world. Over the last decade though, individuals in America
              have been slandered, fired, charged, and even jailed for simply
              voicing their opinions.
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              That is not an America our Founding Fathers would recognize.
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              It is the responsibility of every government official to
              continuously work to preserve and protect the freedom for
              Americans to exercise their free speech.
            </p>
            <p className="mb-2 font-bold text-black pt-2">10</p>
            <p className="mb-4">
              Fatima Hassouna was the subject of a documentary set to play at
              the Cannes Film Festival. She was a Palestinian photojournalist. A
              few days ago she was killed in a direct air strike, which also
              killed nine members of her family.
              <FootnoteLink url="https://www.theguardian.com/world/2025/apr/18/gaza-photojournalist-killed-by-israeli-airstrike-fatima-hassouna" />
            </p>
            <p className="mb-4">
              “If I die, I want a loud death,” she had said. I think: How in
              peril must one be, to forecast what death they might have — that
              their death won’t do enough?
            </p>
            <p className="mb-2 font-bold text-black pt-2">11</p>
            <p className="mb-4">
              A video from Al-Shujaiya shows an Israeli tank emptying its
              ammunition into the concrete support columns of a building.
              <FootnoteLink url="https://x.com/trackingisrael/status/1912899932081500513" />{" "}
              They cheered when the building collapsed.
            </p>
            <p className="mb-4">
              The video is part of a series made by soldiers: shooting buildings
              into rubble.
              <FootnoteLink url="https://x.com/trackingisrael/status/1875193815457513627" />
            </p>
            <p className="mb-4">
              I remember a headline from months ago:
              <FootnoteLink url="https://www.newarab.com/news/gazas-shujaiya-destroyed-ruins-following-israeli-assault" />{" "}
              Gaza City's Shujaiya "destroyed, in ruins" following intense
              Israeli assault. Around that same time, the I.D.F. said that it
              had "completed its mission" in Shujaiya.
              <FootnoteLink url="https://www.al-monitor.com/originals/2024/07/israeli-army-says-completed-operations-gaza-citys-shujaiya" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">12</p>
            <p className="mb-4">
              {" "}
              I wrote in my notes from this day: I saw that two children,
              Abdallah and Mayar, were killed by an airstrike. By just looking
              up their names, I couldn’t find evidence of their death. I almost
              deleted the line, but I wondered about the consequences of doing
              so, whose death I was eradicating. Maybe these were their names;
              in any case, every child is innocent.
            </p>
            <p className="mb-2 font-bold text-black pt-2">13</p>
            <p className="mb-4">
              In early 2019, Netanyahu was quoted saying that those who oppose a
              Palestinian state should support the transfer of funds to Gaza.
              <FootnoteLink url="https://www.timesofisrael.com/for-years-netanyahu-propped-up-hamas-now-its-blown-up-in-our-faces/" />{" "}
              If Israel could prop up Hamas, it would push Gazan ideology from
              that of the West Bank, further preventing the establishment of a
              Palestinian state.
            </p>
            <p className="mb-2 font-bold text-black pt-2">14</p>
            <p className="mb-4">
              Indeed, there has been a number of articles about the fact that
              Israel promoted the success of Hamas.
              <FootnoteLink url="https://theintercept.com/2018/02/19/hamas-israel-palestine-conflict/" />{" "}
              Evidence from this stems from as far back as 1981,
              <FootnoteLink url="https://www.nytimes.com/1981/03/28/world/under-gaza-s-calm-surface-death-drugs-intrigue.html" />{" "}
              and even comes from a number of people across the political aisle.
              Ron Paul (a man about whom I have very little to say) gave a
              speech that offered many of the same points, condemning the U.S.
              and Israel's involvement:
              <FootnoteLink url="https://www.youtube.com/watch?v=KFaSzU9XF5o" />
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              [The U.S.] indirectly and directly through Israel helps establish
              Hamas, then we [support a democratic] election. But then Hamas
              becomes dominant, then we have to kill them.
            </p>
            <p className="mb-4">
              In any case, I feel no need to justify in one way or another how
              we got here. I merely know that this war was hardly about hostages.
              It has been about conquering the land, to one day claim Gaza as
              part of the promised land.
            </p>
            <p className="mb-2 font-bold text-black pt-2">15</p>
            <p className="mb-4">
              Israel’s defense minister Israel Katz confirmed, in writing, that
              Israel’s policy is clear: "no humanitarian aid will enter Gaza."
              <FootnoteLink url="https://www.youtube.com/watch?v=ouS-5mc7tA8" />
              <FootnoteLink url="https://x.com/Israel_katz/status/1912420672401285534" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">16</p>
            <p className="mb-4">
              Have you conceived, really and truly, what it is like for no aid
              to enter a zone of crisis? I have watched hundreds of children
              die, doing nothing about it.
            </p>
            <p className="mb-2 font-bold text-black pt-2">17</p>
            <p className="mb-4">
              A protestor was tased at a town hall for Marjorie Taylor Greene.
              “Because nothing says ‘small government and free speech’ like
              electrocuting dissent in a public forum.”
              <FootnoteLink url="https://x.com/allenanalysis/status/1912276096000667902/" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">18</p>
            <p className="mb-4">
              A message from the family of a hostage: “The prime minster told me
              Israel knows where captives are, is keeping distance.”
              <FootnoteLink url="https://www.timesofisrael.com/liveblog_entry/hostages-mother-pm-told-me-israel-knows-where-captives-are-is-keeping-distance/" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">19</p>
            <p className="mb-4">
              It is reported that Israel launched a direct attack on one of the hostages, Edan
              Alexander. Hamas has lost contact with him.
              <FootnoteLink url="https://www.bbc.co.uk/news/articles/cyvqnd7y5n0o" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">20</p>
            <p className="mb-4">
              In Massachusetts, I.C.E. agents broke the windows of a vehicle to
              detain an immigrant who had no criminal record in the state.
              <FootnoteLink url="https://newbedfordlight.org/lawyer-federal-agents-detain-guatemalan-man-29-with-no-criminal-record/" /> {" "}
              At times, I can’t fathom how this might happen, yet other details
              save this from impossibility: “they blocked the car that he and his
              wife were driving in … entered the house without a warrant …”{" "}
            </p>
            <p className="mb-2 font-bold text-black pt-2">21</p>
            <p className="mb-4">
              “They can bomb a hospital, but if you say that’s fucked up,
              they’ll deport you to a prison camp in El Salvador –– we might
              live in hell.”
              <FootnoteLink url="https://x.com/BlueCollarBklyn/status/1911899848669876680" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">22</p>
            <p className="mb-4">
              It is shocking that we use a word as gentle as <em>settle</em> for
              the act that it specifies. Israelis enter Palestinian
              neighborhoods (which have often already been blockaded) and either
              raze or take the homes. If Palestinians fight back, they’re
              arrested.
            </p>
            <p className="mb-4">
              The U.K. government at the U.N. Security Council: "The expansion
              of Israeli settlements in the West Bank is wholly unacceptable and
              illegal."
              <FootnoteLink url="https://www.gov.uk/government/speeches/the-expansion-of-israeli-settlements-in-the-west-bank-is-wholly-unacceptable-and-illegal-uk-statement-at-the-un-security-council" />
            </p>
            <p className="mb-4">
              A Haaretz headline: “Palestinian Family Goes Out for Dinner,
              Returns to Find Israeli Settlers Took Over Their Home.”
              <FootnoteLink url="https://www.haaretz.com/israel-news/2025-03-24/ty-article/.premium/palestinian-family-goes-out-for-dinner-returns-to-find-israeli-settlers-took-over-home/00000195-c881-db4f-a3b5-deb504f60000" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">23</p>
            <p className="mb-4">
              I found a post from 2022: A wheelchair-bound 31-year-old woman in
              Toronto got approval for medically-assisted suicide, after a
              fruitless bid to secure an affordable apartment.
              <FootnoteLink url="https://vancouverisland.ctvnews.ca/health/article/woman-with-disabilities-nears-medically-assisted-death-after-futile-bid-for-affordable-housing/" />
            </p>
            <p className="mb-4">
              "I was scared that they weren't going to say ‘yes’," she said.
            </p>
            <p className="mb-2 font-bold text-black pt-2">24</p>
            <p className="mb-4">
                I remember a quote by Annie Ernaux, from her book <em>Simple Passion</em>: 
                </p>
                <p className="pl-6 italic text-gray-600 mb-4">
                              Naturally I feel no shame in writing these things because of the time which separates the moment when they are written — when only I can see them — from the moment when they will be read by other people, a moment which I feel will never come. By then I could have had an accident or died; a war or a revolution could have broken out. This delay makes it possible for me to write today, in the same way I used to lie in the scorching sun for a whole day at sixteen, or make love wihout contraceptives at twenty: without thinking about the consequences.
            </p>
            <p className="mb-2 font-bold text-black pt-2">25</p>
            <p className="mb-4">
              Back in October 2023, it was reported that B.B.C. journalists were
              “crying in the toilets” because they were forced to put out
              propaganda for Israel.
              <FootnoteLink url="https://www.dailysabah.com/world/mid-east/bbc-journalists-reportedly-left-crying-over-biased-gaza-coverage" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">26</p>
            <p className="mb-4">
              “My husband died before my eyes, My son's legs were cut off. I
              tied his legs with my hijab & carried him for 4 kilometers. My son
              then also passed away."
              <FootnoteLink url="https://www.instagram.com/p/DIGi8foz9K1/" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">27</p>
            <p className="mb-4">
              Trump threatened that citizens will be the next batch sent to El
              Salvador. “Home-growns are next. The home-growns. You got to build
              about five more places. It’s not big enough.”
              <FootnoteLink url="https://www.prisonpolicy.org/blog/2025/04/17/home-growns-are-next/" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">28</p>
            <p className="mb-4">
              In the West Bank, Palestinian child Malik al Hattab was shot by a
              sniper in Al Jalazoun refugee camp.
              <FootnoteLink url="https://www.dci-palestine.org/israeli_forces_kill_palestinian_child_with_expanding_bullet_in_jalazone_refugee_camp_raid" />
              <FootnoteLink url="https://x.com/AbujomaaGaza/status/1911850514960031962" />{" "}
              The image haunts me.
            </p>
            <p className="mb-2 font-bold text-black pt-2">29</p>
            <p className="mb-4">
              Kilmar Abrego Garcia was deported without a hearing to a
              mega-prison in El Salvador.
            </p>
            <p className="mb-4">
              “The administration previously conceded Ábrego García’s
              deportation was an 'administrative error,' but it has since
              refused to bring him back and dug in on its contention that it
              should not be responsible for his repatriation.”
              <FootnoteLink url="https://www.theguardian.com/us-news/2025/apr/16/who-is-kilmar-abrego-garcia" />
            </p>
            <p className="mb-4">
              The U.S. Supreme Court ruled that it was a violation of due
              process.
              <FootnoteLink url="https://docs.google.com/document/d/1A_ZJWTLMF-ifP7sYmQsAOY1FQliJo1DwNVeSjQDEaPs/edit?tab=t.0" />
            </p>
            <p className="mb-4">
              Trump said: “The Supreme Court, it was 9-0?” Miller: “Yes, in our
              favor,” a lie.
            </p>
            <p className="mb-2 font-bold text-black pt-2">30</p>
            <p className="mb-4">
              U.S. Senator Chris Murphy wrote the following about Abrego Garcia.
              <FootnoteLink url="https://x.com/ChrisMurphyCT/status/1911868732009295961" />
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              [He] came to the U.S. over 10 years ago. He married a U.S. citizen
              and has three U.S. citizen children. But he put in removal
              proceedings because he didn't apply for asylum in time and because
              of alleged ties to drug gangs. He denies any gang affiliation.
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              You can think whatever you want about the merits of him staying
              (his U.S. family, his steady job) or being deported (the alleged
              gang affiliation), there's really only one key fact:
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              A court ruled he cannot be deported to El Salvador because he
              would face threat of death there.
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              Trump's people are engaged in nuclear grade gaslighting. They say
              it's up to El Salvador if they want to send him back. Bullshit. We
              bring people back all the time when we wrongfully remove them.
              They could get this guy in a hot second if they wanted to.
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              If Trump can lock up of remove anyone, then we are all at grave
              risk.
            </p>
            <p className="mb-2 font-bold text-black pt-2">31</p>
            <p className="mb-4">
              It has been reported that effective July 2025, X (Twitter) will
              become the official and only communication system for the Social
              Security Administration and Medicare.
              <FootnoteLink url="https://www.snopes.com/news/2025/04/15/ssa-using-x-communications/" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">32</p>
            <p className="mb-4">
              “Israelis poison beehives owned by a Palestinian farmer in Farsh
              Al-Hawa village in al-Khalil. Killing bees in this day and age is
              a crime against humanity.”
              <FootnoteLink url="https://x.com/swilkinsonbc/status/1911809342162022671" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">33</p>
            <p className="mb-4">
              Over a thousand international students have lost their student
              visas for participating in Palestinian protests.
              <FootnoteLink url="https://www.aljazeera.com/news/2025/4/18/us-revokes-nearly-1500-student-visas-who-are-the-targets" />{" "}
              Trump and his administration have alleged that these students
              spread anti-Semitism on campus, even though Jewish activists are
              often at the forefront of many protests.
            </p>
            <p className="mb-2 font-bold text-black pt-2">34</p>
            <p className="mb-4">
              A headline at The Guardian:
              <FootnoteLink url="https://www.theguardian.com/us-news/2025/apr/19/aditya-wahyu-harsono-immigration-indonesia" />
              “Aditya Wahyu Harsono, father of infant with special needs,
              surprised at work [by I.C.E.] despite valid visa through June
              2026.”{" "}
            </p>
            <p className="mb-4">
              He told the agents that he had a valid student visa through June
              2026, as well as a pending green-card application because of his
              marriage to a citizen. But he was told that he had overstayed, and
              was being deported.
            </p>
            <p className="mb-4">
              “Harsono, a practicing Muslim, is also known for his vocal support
              of Gaza on social media and runs a nonprofit that donates proceeds
              from art sales to Palestinian aid organizations.”
              <FootnoteLink url="https://www.financialexpress.com/world-news/f-1-visa-pulled-without-indonesian-man-detained-by-ice-in-us-family-left-shocked/3815912/" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">35</p>
            <p className="mb-4">
              Israel bombed tents and an UNRWA flour distribution point in Bani
              Suheila, east of Khan Younis.
              <FootnoteLink url="https://www.youtube.com/watch?v=LQpOX-6kRBE" />
              <FootnoteLink url="https://x.com/SuppressedNws/status/1911411960807108806" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">36</p>
            <p className="mb-4">
              A video surfaced from November 2024, of an Israeli soldier
              recording himself in a bulldozer, razing a shrine to Saint George
              in the south of Lebanon.
              <FootnoteLink url="https://today.lorientlejour.com/article/1455999/did-the-israeli-army-demolish-a-statue-of-saint-george-in-south-lebanon.html" />{" "}
              I could look up in what manner this is a war crime, but right now,
              I am exhausted (who am I letting down by not knowing the exact
              law? And what is the point of such a law, now?).
            </p>
            <p className="mb-2 font-bold text-black pt-2">37</p>
            <p className="mb-4">
              A video of Mohsen Madawi, a Palestinian-born Green Card holder,
              who was apprehended by I.C.E. in the middle of his interview to
              become an American citizen.
              <FootnoteLink url="https://edition.cnn.com/2025/04/14/us/mohsen-mahdawi-columbia-university-trump/index.html" />{" "}
              He had done a segment with 60 Minutes to discuss the protests; in
              the show, he is very anti-violent, and supports justice for all.
              <FootnoteLink url="https://www.youtube.com/watch?v=grj328-hlhA" />{" "}
              And yet — he is the one who was detained. A commenter said:
              <FootnoteLink url="https://x.com/JeninYounesEsq/status/1911881535730446529" />
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              It's always been clear that the Trump Admin's monstrous campaign
              is about stamping out pro-Palestinian activity. I initially
              thought they made a mistake with Khalil, and accidentally picked
              someone who had not said something even remotely objectionable or
              committed any crime. Now it's clear that they're targeting those
              people specifically, to entirely chill any pro-Palestinian
              advocacy.
            </p>
            <p className="mb-2 font-bold text-black pt-2">38</p>
            <p className="mb-4">
              El Salvador President Bukele says immigrant from Maryland won't be
              returned to the U.S.
              <FootnoteLink url="https://www.youtube.com/watch?v=_Gm_XCqB5P0" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">39</p>
            <p className="mb-4">
              Two headlines side by side: “Gaza hospital hit by Israeli strike,
              Hamas says.” And then another, about the Ukraine war: “Russia
              destroys hospital in latest missile attack.”
              <FootnoteLink url="https://x.com/kennardmatt/status/1911397518220447986" />{" "}
              I don’t know how to keep on explaining, keep on living through
              this.
            </p>
            <p className="mb-2 font-bold text-black pt-2">40</p>
            <p className="mb-4">
              At the University of Amsterdam, undercover police officers
              assaulted protesters.
              <FootnoteLink url="https://www.youtube.com/watch?v=QqG0IEkMYYE" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">41</p>
            <p className="mb-4">
              A headline for The City, a newspaper based in New York City:
              “Merwil Gutiérrez had no criminal record when I.C.E. agents
              detained the 19-year-old outside his home. Now his father, Wilmer,
              is still searching for answers.”
              <FootnoteLink url="https://www.thecity.nyc/2025/04/14/bronx-ice-merwil-gutierrez-el-salvador/" />
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              Wilmer only found out his son had been detained after receiving a
              phone call on March 15 from his nephew, Luis, who lives with them.
              That morning was their last time together; they had gone around
              the corner to do their laundry. Later that day, Wilmer said that
              his son met with a friend to get help with some errands at the
              American Red Cross. He learned this from Luis, who looked at the
              situation from inside the apartment: when his son was on his way
              back, just steps from his home when I.C.E. agents stopped him.
              "The officers grabbed him and two other boys right at the entrance
              to our building. One said, 'No, he's not the one,' like they were
              looking for someone else. But the other said, 'Take him anyway.'
            </p>
            <p className="mb-2 font-bold text-black pt-2">42</p>
            <p className="mb-4">
              The ACLU:
              <FootnoteLink url="https://www.aclusocal.org/en/news/immigrant-children-do-not-have-right-attorney-unless-they-can-pay-rules-appeals-court#:~:text=On%20September%2019%2C%202018%2C%20the,a%20panel%20of%2011%20judges." />
              "Immigrant Children Do Not Have the Right to an Attorney Unless
              They Can Pay, Rules Appeals Court."
            </p>
            <p className="mb-2 font-bold text-black pt-2">43</p>
            <p className="mb-4">
              “U.S. judge rules Mahmoud Khalil can be deported for his views.”
              <FootnoteLink url="https://www.theguardian.com/us-news/2025/apr/11/mahmoud-khalil-deportation-ruling-immigration" />{" "}
              He has never been accused of a crime.
            </p>
            <p className="mb-2 font-bold text-black pt-2">44</p>
            <p className="mb-4">
              The Al-Ahli Baptist Hospital was completely knocked out of service
              by an Israeli air strike.
              <FootnoteLink url="https://news.un.org/en/story/2025/04/1162191" />{" "}
              This is the same hospital that Israel bombed in October 2023,
              killing hundreds of Palestinians,
              <FootnoteLink url="https://www.nytimes.com/2023/10/19/world/middleeast/gaza-hospital-blast-deaths.html" />{" "}
              and for which they waged a massive disinformation campaign to
              blame Hamas.
              <FootnoteLink url="https://forensic-architecture.org/investigation/israeli-disinformation-al-ahli-hospital" />
              <FootnoteLink url="https://www.euronews.com/my-europe/2023/10/20/fake-social-media-accounts-used-to-spread-disinformation-after-the-gaza-hospital-blast" />{" "}
              Now, because the world has allowed this disaster, there was no
              point in a masquerade, no need for lies.
            </p>
            <p className="mb-2 font-bold text-black pt-2">45</p>
            <p className="mb-4">
              Ghassan Kanafani, the famous Palestinian author, wrote several
              books; they’ve been translated into 17 languages. His stories were
              inspired by being forced out of his homeland when he was a child.
            </p>
            <p className="mb-4">
              He never carried a weapon or fired one, he was merely a writer,
              fighting with stories.
              <FootnoteLink url="https://www.palquest.org/en/biography/6566/ghassan-kanafani" />{" "}
              Israel considered him a terrorist, and killed him and his
              17-year-old niece in 1972 with a car bomb.
            </p>
            <p className="mb-2 font-bold text-black pt-2">46</p>
            <p className="mb-4">
              There is a politics of “baseless delays” in the West Bank; this is
              a term that has stuck with me, though I don’t remember when I
              first read it. Every checkpoint is a friction, every arrest is a
              nuisance, every bullet injury a minor trauma. I considered looking
              for a source for this, but I had the feeling I would pick one at
              random — there are too many.
            </p>
            <p className="mb-2 font-bold text-black pt-2">47</p>
            <p className="mb-4">
              Israeli-Russian activist Andrey X reports on settlements.
              <FootnoteLink url="https://www.agos.com.tr/en/article/31863/a-life-without-borders-what-is-happening-after-ceasefire-in-the-west-bank" />{" "}
              He said in an interview:
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              The settlers come and just assault Palestinian soil and
              Palestinians as well. This is a recurring case; I've seen happen
              over and over again in the West Bank. When the police arrive, they
              arrest the Palestinians who were attacked. I've been assaulted and
              arrested on charges of assault. The police here are worse, because
              they do not enforce any specific system. The police can do
              whatever they want just like a mafia system.
            </p>
            <p className="mb-2 font-bold text-black pt-2">48</p>
            <p className="mb-4">
              DropSite news reporter Ryan Grim wrote about Fatima Hassouna, the subject of
              the film at Cannes:
              <FootnoteLink url="https://x.com/ryangrim/status/1913319777944076500" />
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              For this to have been a deliberate act –– which it plainly was ––
              consider what that means. A person within the I.D.F. saw the news
              that Fatima's film was accepted into Cannes. He/she/they then
              proposed assassinating her. Other people reviewed the suggestion
              and approved it. Then other people carried it out.
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              If I am wrong, please share the statement of regret, and the
              pledge to investigate, from the I.D.F.
            </p>
            <p className="mb-2 font-bold text-black pt-2">49</p>
            <p className="mb-4">
              There must be some rule, somewhere, that settlements cannot be
              built on the bodies of dead children. There must be.
            </p>
            <p className="mb-2 font-bold text-black pt-2">50</p>
            <p className="mb-4">
              I think about all the details I had memorized of the ceasefire
              deals, that have all now gone like wind. There were always new
              conditions, new rules; a series of impossible demands, the
              rejections of which propelled Gaza into darker and darker
              massacres.
            </p>
            <p className="mb-2 font-bold text-black pt-2">51</p>
            <p className="mb-4">
              "Without our captors, we would have been killed by the fire of our
              own forces," said a freed Israeli hostage.
              <FootnoteLink url="https://www.trtworld.com/middle-east/freed-israeli-hostage-says-hamas-protected-them-during-israels-air-strikes-16374456" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">52</p>
            <p className="mb-4">
              Hind Rajab cried in the car for help. She called the Palestinian
              Red Crescent Society. She was shot at 355 times.
              <FootnoteLink url="https://www.hindrajabfoundation.org/" />
              <FootnoteLink url="https://forensic-architecture.org/investigation/the-killing-of-hind-rajab" />
              <FootnoteLink url="https://www.ohchr.org/en/press-releases/2024/07/gaza-killing-hind-rajab-and-her-family-war-crime-too-many-warn-experts" />
            </p>
            <p className="mb-4">
              “They found the black Kia car Hind had been travelling in –– its
              windscreen and dashboard smashed to pieces, bullet holes scattered
              across the side.”
              <FootnoteLink url="https://forensic-architecture.org/investigation/the-killing-of-hind-rajab" />
              <FootnoteLink url="https://www.bbc.co.uk/news/world-middle-east-68261286" />
              <FootnoteLink url="https://www.ohchr.org/en/press-releases/2024/07/gaza-killing-hind-rajab-and-her-family-war-crime-too-many-warn-experts" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">53</p>
            <p className="mb-4">
              At number 53, I feel like collapsing, just like I have many times
              before. I don't know how those poor people in Gaza continue to
              wake up and have nothing to do, no one helping them, they don't
              even have food.
            </p>
            <p className="mb-2 font-bold text-black pt-2">54</p>
            <p className="mb-4">
              “Israeli soldiers assault Palestine TV crew in Jenin, unaware
              they're on live TV, and demand to confiscate phones of the
              journalist.”
              <FootnoteLink url="https://x.com/JalalAK_jojo/status/1911047749627158556" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">55</p>
            <p className="mb-4">
              An image of the Star of David shaved onto the head of Palestinian
              hostage Mus'ab Qatawi.
              <FootnoteLink url="https://www.middleeasteye.net/news/israel-prison-palestinian-star-david-shaved-head" />{" "}
              The report says that his head was forced into a garbage can.{" "}
            </p>
            <p className="mb-4">
              “He was released in the same batch as Ahmad Manasra, who spent a
              decade behind bars after being arrested at the age of 13 and
              suffered a similar treatment before his release.” (Note
              “Palestinian hostage” sounds odd –– this is not the language the
              media like to use.)
            </p>
            <p className="mb-2 font-bold text-black pt-2">56</p>
            <p className="mb-4">
              I think again about what was written in plain English: Khalil
              isn’t accused of breaking any laws during his protests at
              Columbia.{" "}
            </p>
            <p className="mb-2 font-bold text-black pt-2">57</p>
            <p className="mb-4">
              In Gaza, a larger number of children are dead and buried after
              having been murdered by Israel in the past year than the number
              currently attending school.
              <FootnoteLink url="https://www.bbc.co.uk/news/articles/c0r5827dke1o" />{" "}
              Almost 90% of schools are damaged or destroyed.
              <FootnoteLink url="https://www.savethechildren.net/blog/education-under-attack-gaza-nearly-90-school-buildings-damaged-or-destroyed" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">58</p>
            <p className="mb-4">
              A photo of a banner on a rooftop in Beit Lahia: in both Hebrew and
              English, and with an American flag: “School, please don’t bomb.”
              <FootnoteLink url="https://www.facebook.com/alkabeer.alhani/posts/heres-an-enhanced-version-of-your-messageyesterday-our-family-opened-a-school-fo/8204666662963740/" />
            </p>
            <p className="mb-4">
              “Yesterday, our family opened a school for 280 children, ages 6 to
              10, from first to fourth grade, with a dedicated staff of 15.
              Today marked the second day of classes, and the kids didn’t want
              to leave — they’ve already found comfort in this new space. As an
              additional safety measure, we’ve placed a large sign on the roof,
              visible from the sky, in hopes of protecting everyone. The jury is
              still out on whether this will help keep us safe, but at the very
              least, we made our plea: 'please.’”
            </p>
            <p className="mb-4">
              Israel bombed the school anyway.
              <FootnoteLink url="https://tiktokgenocide.com/uploads/israel-bombs-school-despite-clear-sign-school-please-dont-bomb" />
            </p>
            <p className="mb-4">
              “We've got nothing to do with any political party or government,
              and still targeted us. We put over there the flag and a sign
              saying it's a school, and they struck the sign precisely.”
            </p>
            <p className="mb-2 font-bold text-black pt-2">59</p>
            <p className="mb-4">
              Israeli soldiers with Breaking the Silence reported that Gaza’s
              perimeter was turned into a massive ‘kill zone’ –– where they were
              commanded to kill everything, and everyone, within a kilometer of
              the border.
              <FootnoteLink url="https://www.breakingthesilence.org.il/inside/wp-content/uploads/2025/04/Perimeter_English-2.pdf" />{" "}
              The report is harrowing.
            </p>
            <p className="mb-4">
              The soldiers detail how Israeli forces destroyed homes, factories,
              mosques, schools, and farmland up to 1.5 km inside Gaza’s
              perimeter, creating what soldiers called “a death zone of enormous
              proportions.” Palestinians didn’t know it was a death zone — if
              they entered, they were shot.
            </p>
            <p className="mb-4">
              Soldiers were told to “make Gaza pay a ‘territorial price’ for the
              October 7th attacks.”
            </p>
            <p className="mb-4">
              Destruction was deliberate, needless: “Unlike many other combat
              zones in the Gaza Strip, the annihilation of infrastructure and
              buildings in the perimeter sometimes occurred after the area was
              captured, when no immediate or concrete threat to the forces was
              present.”
            </p>
            <p className="mb-4">
              “A lot of us went there, I went there, because they killed us and
              now we’re going to kill them,” they said. “And I found out that
              we’re not only killing them — we’re killing them, we’re killing
              their wives, their children, their cats, their dogs. We’re
              destroying their houses and pissing on their graves.”
              <FootnoteLink url="https://www.theguardian.com/world/2025/apr/07/israel-military-gaza-perimeter-land-testimony-report" />
            </p>
            <p className="mb-4">
              The I.D.F. has not, and still has not, commented on the report.
            </p>
            <p className="mb-2 font-bold text-black pt-2">60</p>
            <p className="mb-4">
              A soldier told his story from being on the ground:
              <FootnoteLink url="https://www.haaretz.com/opinion/2024-12-23/ty-article-opinion/.premium/when-you-enter-gaza-you-are-god-inside-the-minds-of-idf-soldiers-who-commit-war-crimes/00000193-f2a4-dc18-a3db-fee62b540000" />
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              A new commander came to us. We went out with him on the first
              patrol at six in the morning. He stops. There's not a soul in the
              streets, just a little 4-year-old boy playing in the sand in his
              yard. The commander suddenly starts running, grabs the boy, and
              breaks his arm at the elbow and his leg here. Stepped on his
              stomach three times and left. We all stood there with our mouths
              open. Looking at him in shock ... I asked the commander: "What's
              your story?" He told me: These kids need to be killed from the day
              they are born. When a commander does that, it becomes legit."
            </p>
            <p className="mb-2 font-bold text-black pt-2">61</p>
            <p className="mb-4">
              “These kids need to be killed from the day they are born.”
            </p>
            <p className="mb-2 font-bold text-black pt-2">62</p>
            <p className="mb-4">
              Five I.D.F. soldiers were indicted for sexually abusing
              Palestinian hostages.
              <FootnoteLink url="https://www.timesofisrael.com/5-idf-reservists-indicted-for-severe-abuse-of-palestinian-detainee-at-sde-teiman/" />{" "}
            </p>
            <p className="mb-2 font-bold text-black pt-2">63</p>
            <p className="mb-4">
              “On Monday, a day after TikTok came back online for the U.S.,
              people started noticing that the app would not let them comment
              ‘Free Palestine.’”
              <FootnoteLink url="https://www.404media.co/tiktok-free-palestine-comments-removed/#:~:text=When%20TikTok%20returned%20after%20being,comments%20were%20being%20instantly%20removed.&text=404%20Media%20is%20an%20independent,and%20owned%20by%20human%20journalists." />{" "}
            </p>
            <p className="mb-2 font-bold text-black pt-2">64</p>
            <p className="mb-4">
              Ahmad Manasra was made a political prisoner at age 13, for being
              present at a stabbing attack against two Israelis at the Pisgat
              Ze’ev settlement.
              <FootnoteLink url="https://www.aljazeera.com/news/2016/1/17/palestinian-teen-could-face-maximum-sentence" />{" "}
              “Ahmad was charged with attempted murder even though he did not
              stab anyone — something that the courts had acknowledged.”
              <FootnoteLink url="https://www.aljazeera.com/news/2025/4/10/palestinian-ahmad-manasra-released-from-israeli-prison-after-nine-years" />{" "}
            </p>
            <p className="mb-4">
              10 years later, the I.D.F. said they would release him. On April
              10, 2025, the Israeli prison authorities freed Ahmad “away from
              the Nafha Prison [facility] to prevent his family from receiving
              him, leaving him alone in an empty area.”
              <FootnoteLink url="https://www.aljazeera.com/news/2025/4/10/palestinian-ahmad-manasra-released-from-israeli-prison-after-nine-years" />
            </p>
            <p className="mb-4">
              A passerby found him and reported him to local authorities, after
              which he was reunited with his family.
            </p>
            <p className="mb-2 font-bold text-black pt-2">65</p>
            <p className="mb-4">
              I can’t believe I’m watching the Star of David burned into the
              remnants of buildings, etched into someone’s skull.
              <FootnoteLink url="https://x.com/muhammadshehad2/status/1910675546003882458" />{" "}
              If anything is antisemitic, it must be this.
            </p>
            <p className="mb-2 font-bold text-black pt-2">66</p>
            <p className="mb-4">
              April 10 was Rachel Corrie’s birthday. 22 years ago, a bulldozer
              ran her over while she protested against the demolishment of homes
              in Rafah,
              <FootnoteLink url="https://rachelcorriefoundation.org/rachel" />{" "}
              the city the I.D.F. has now destroyed.
              <FootnoteLink url="https://mondoweiss.net/2025/04/rafah-no-longer-exists-this-is-part-of-israels-plan-to-permanently-occupy-gaza/" />{" "}
              She was an American citizen. Both the American and Israeli
              investigations were inconsequential.
              <FootnoteLink url="https://www.theguardian.com/world/2012/aug/24/israel-rachel-corrie-us-ambassador" />
            </p>
            <p className="mb-4">
              “The Palestinian physician from Gaza who had examined Corrie's
              wounds on the scene was unable to testify after Israel refused him
              an entry visa and rejected an application for him to testify by
              video link.”
              <FootnoteLink url="https://en.wikipedia.org/wiki/Rachel_Corrie#Subsequent_events" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">67</p>
            <p className="mb-4">
              Israel’s Finance Minister Bezalel Smotrich: "Not even a grain of
              wheat will enter Gaza."
              <FootnoteLink url="https://www.middleeasteye.net/news/smotrich-says-not-even-grain-wheat-will-enter-gaza" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">68</p>
            <p className="mb-4">
              Israel’s envoy to Austria suggested that Gazan children should be
              executed before becoming militants, in a secret recording that
              leaked to the internet.
              <FootnoteLink url="https://www.middleeasteye.net/live-blog/live-blog-update/israeli-envoy-austria-suggests-executing-gaza-children-secret-0" />{" "}
              Someone commented: "That's just what they've been doing. Who needs
              to see a secret recording?"
            </p>
            <p className="mb-2 font-bold text-black pt-2">69</p>
            <p className="mb-4">
              Israeli journalist Tamir Morag: "Gaza is a cage inside a zoo. A
              human being who grows up in a cage inside a zoo, in the end,
              behaves like a human animal.”
              <FootnoteLink url="https://x.com/GozukaraFurkan/status/1908845326825292052" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">70</p>
            <p className="mb-4">
              Art Spiegelman, the man who wrote the seminal Holocaust text{" "}
              <em>Maus</em>, is now working on a book on Gaza.
              <FootnoteLink url="https://forward.com/fast-forward/685537/art-spiegelman-gaza-hamas-israel-graphic-novel/" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">71</p>
            <p className="mb-4">
              I watched a video of synthetic sewage being sprayed over a
              Palestinian neighborhood in East Jerusalem.
              <FootnoteLink url="https://x.com/arixegal/status/1910064701284950516" />{" "}
              There is a name for this noxious chemical: "skunk"; and the
              I.D.F. have been using it for years.
              <FootnoteLink url="https://www.bbc.co.uk/news/magazine-34227609" />
            </p>
            <p className="mb-4">
              An I.D.F. spokesperson said that skunk is "an effective,
              non-lethal, riot dispersal means." There was no reported unrest in East
              Jerusalem prior to the video being taken.
            </p>
            <p className="mb-2 font-bold text-black pt-2">72</p>
            <p className="mb-4">
              In East Jerusalem, U.S. citizen and fifteen-year-old Tariq Abu
              Khdeir was beaten by police. The event was caught on tape.
              <FootnoteLink url="https://www.youtube.com/watch?v=i-ZHhKsSRGo" />{" "}
              For the beating, the police officer received 45 days of community
              service.
              <FootnoteLink url="https://electronicintifada.net/blogs/rania-khalek/us-rebukes-israel-over-light-sentence-officer-who-attacked-tampa-teen" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">73</p>
            <p className="mb-4">
              Israeli terrorists executed Amana Ibrahim Muhammad Yaqoub in the
              West Bank, for involvement in a knife attack.
              <FootnoteLink url="https://www.arabnews.com/node/2596359/middle-east" />
            </p>
            <p className="mb-4">
              “Since the start of the Gaza war in October 2023, violence has
              soared in the occupied West Bank. Israeli troops and settlers have
              killed at least 918 Palestinians in the territory, according to
              health ministry figures. Palestinian attacks and clashes during
              military raids have killed at least 33 Israelis, including
              soldiers, over the same period, according to Israeli figures.”
            </p>
            <p className="mb-2 font-bold text-black pt-2">74</p>
            <p className="mb-4">
              A video: Israel “arrested children for walking on Jews-only road
              in the West Bank.”
              <FootnoteLink url="https://x.com/RyanRozbiani/status/1909621255960998075" />{" "}
              In 2022, Amnesty International wrote a long-form case that
              attempts to prove that Israel is an apartheid regime reigning over
              Palestine.
              <FootnoteLink url="https://www.amnesty.org/en/wp-content/uploads/2022/02/MDE1551412022ENGLISH.pdf" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">75</p>
            <p className="mb-4">
              An article from 2014: "Israel government 'tortures' children by
              keeping them in cages, human rights group says."
              <FootnoteLink url="https://www.independent.co.uk/news/world/middle-east/israel-government-tortures-children-by-keeping-them-in-cages-human-rights-group-says-9032826.html" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">76</p>
            <p className="mb-4">
              In the video of the Israeli soldiers firing on the medical
              workers, one of them is heard crying: "Forgive me, mother, this is
              the path I chose, to help people."{" "}
              <FootnoteLink url="https://www.middleeastmonitor.com/20250406-forgive-me-mother-army-arrived-final-words-of-palestinian-paramedic-moments-before-israeli-execution/" />{" "}
              In the aftermath of the leak, the B.B.C. headline reads: "Video
              footage appears to contradict Israeli account of Gaza medic
              killings."
              <FootnoteLink url="https://www.bbc.co.uk/news/articles/c4g2z103nqxo" />{" "}
              This headline makes me rage. Another one: "Video casts doubt
              on Israeli account of Gaza medic killings."
              <FootnoteLink url="https://www.bbc.co.uk/news/videos/c078kenjgvpo" />{" "}
              I don’t know how many times I can hear the same story, I feel sick
              of everything.
            </p>
            <p className="mb-4">
              Later: “Israel admits mistakes over medic killings in Gaza.”
              <FootnoteLink url="https://www.youtube.com/watch?v=g7JrEKZgBR4" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">77</p>
            <p className="mb-4">
              A whistleblower report was released about the bias against
              Palestinians at the C.B.C., the Canadian news network.
              <FootnoteLink url="https://breachmedia.ca/cbc-whitewashed-israels-crimes-gaza-firsthand/" />{" "}
              A number of stories: pro-Palestine guests not allowed to do live broadcasts,
              then suddenly shows being cancelled; reports on the deaths of guests' family members were “alleged, yet to be
              confirmed.”{" "}
            </p>
            <p className="mb-4">
              For a moment, everything feels connected: “Many journalists just
              follow the path of least resistance. The fact that permanent,
              full-time C.B.C. jobs are in such short supply, combined with
              threats of looming cuts, only reinforces this problem.” Then I
              resume my disbelief that this cannot be our world, I can’t fathom
              how we got here. I remember C.N.N. enforced much of the same.
              <FootnoteLink url="https://www.theguardian.com/media/2024/feb/04/cnn-staff-pro-israel-bias" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">78</p>
            <p className="mb-4">
              C.N.N. headline: "A video has emerged showing the final moments of
              more than a dozen Palestinian emergency workers shot dead by
              Israeli troops in Gaza last month, casting doubt on Israeli claims
              that soldiers opened fire on vehicles 'advancing suspiciously.'"
            </p>
            <p className="mb-2 font-bold text-black pt-2">79</p>
            <p className="mb-4">
              The U.S. bombed the water source for over 50,000 civilians in
              Yemen.
              <FootnoteLink url="https://www.theguardian.com/world/2025/apr/02/us-bombing-of-yemen-houthis" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">80</p>
            <p className="mb-4">
              “Autopsy of Palestinian minor detainee raises suspicion of
              starvation, severe medical neglect.”
              <FootnoteLink url="https://www.haaretz.com/israel-news/2025-04-03/ty-article/.premium/palestinian-minor-detainee-dies-in-israeli-prison-autopsy-shows-severe-medical-neglect/00000195-fb95-d101-a7bd-fbbfed960000" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">81</p>
            <p className="mb-4">
              Assal Rad: “You don't kill over 17,000 children by accident, you
              kill children when you want to wipe out a people's future.”
              <FootnoteLink url="https://x.com/AssalRad/status/1908280488206491927" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">82</p>
            <p className="mb-4">
              When it released to the public, the video of the U.S. firing on
              Iraqi citizens shook the world.
              <FootnoteLink url="https://www.youtube.com/watch?v=UaqY12VHFv4" />{" "}
              The soldiers –– and those who leaked it –– were royally punished.
              But I feel like I have seen this story hundreds of times in Gaza,
              and no one seems to care.
            </p>
            <p className="mb-2 font-bold text-black pt-2">83</p>
            <p className="mb-4">
              I watched a video of a bomb going off, and human bodies then
              soaring into the air.
              <FootnoteLink url="https://x.com/7MohammedKhaled/status/1908252090238980099" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">84</p>
            <p className="mb-4">
              Today I saw a three-month old baby beheaded with my own eyes;
              right now, I want to vomit.
              <FootnoteLink url="https://x.com/ceolawyer/status/1907412231291093386" />{" "}
              I remember that I was supposed to believe that Hamas beheaded
              babies –– now refuted.
              <FootnoteLink url="https://en.wikipedia.org/wiki/Hamas_baby_beheading_hoax" />
              <FootnoteLink url="https://www.aljazeera.com/news/2023/10/12/white-house-walks-back-bidens-claim-he-saw-children-beheaded-by-hamas" />
              <FootnoteLink url="https://www.aa.com.tr/en/world/cnn-reporter-apologizes-for-defending-israeli-claims-that-hamas-beheaded-babies-/3019286" />
              <FootnoteLink url="https://www.haaretz.com/israel-news/2024-01-21/ty-article/.premium/army-officer-makes-incorrect-claims-on-oct-7-massacre-idf-well-set-record-straight/0000018d-2c67-daf5-a1bf-ac77f9b50000" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">85</p>
            <p className="mb-4">
              “Israel attacks Gaza school shelter again after dozens killed.”
              <FootnoteLink url="https://www.aljazeera.com/news/liveblog/2025/4/3/live-israel-kills-almost-80-palestinians-in-gaza-attacks-bombs-un-clinic?update=3621687" />
            </p>
            <p className="mb-2 font-bold text-black pt-2">86</p>
            <p className="mb-4">
              A Palestinian child was tortured to death in Israeli prison. The
              A.P. merely writes: “A Palestinian from the West Bank is first
              detainee under 18 to die in Israeli prison, officials say.”
              <FootnoteLink url="https://apnews.com/article/israel-hamas-palestinian-prisoner-teenager-dead-874271e9c7afe095310143233aec0d38?taid=67ebf3dae1586a00015f6f60&utm_campaign=TrueAnthem&utm_medium=AP&utm_source=Twitter" />{" "}
            </p>
            <p className="mb-2 font-bold text-black pt-2">87</p>
            <p className="mb-4">
              An Israeli soldier came forward and said he was commanded to use a
              Palestinian as a human shield.
              <FootnoteLink url="https://www.cbsnews.com/news/israeli-soldier-palestinians-human-shields-gaza/" />
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              "We've burned down buildings for no reasons, which is violating
              the international law, of course," he told CBS News. "…And we used
              human shields as protection."
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              Tommy said his commander ordered his unit to use Gazan civilians
              to search buildings for explosives instead of dogs.
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              "They were Palestinian," he said. "We sent them in first to see if
              the building was clear and check for booby traps…They were
              trembling and shaking."
            </p>
            <p className="pl-6 italic text-gray-600 mb-4">
              "We talked to our commander, and we asked him to stop doing it,"
              Tommy said, but they were ordered to continue. He told CBS News it
              was policy.
            </p>
            <p className="mb-2 font-bold text-black pt-2">88</p>
            <p className="mb-4">
              As of February, the I.D.F. had destroyed 79% of Gaza’s mosques.
              <FootnoteLink url="https://www.aa.com.tr/en/middle-east/israel-s-attacks-destroy-79-of-gaza-mosques-3-churches/3469056" />{" "}
              I imagine a military order: Destroy the mosque.
            </p>
            <p className="mb-2 font-bold text-black pt-2">89</p>
            <p className="mb-4">
              On Eid, a volunteer doctor said from Gaza: "Spent the entire
              morning and afternoon cutting brand new Eid clothes off dead
              children."
              <FootnoteLink url="https://www.jewishvoiceforlabour.org.uk/article/eid-brings-more-carnage-in-gaza/?utm_source=mailpoet&utm_medium=email&utm_source_platform=mailpoet&utm_campaign=today-on-the-jvl-blog-newsletter-total-articles-for-you_1" />
            </p>
            <p className="pt-5 mb-4">
              While doing post-writing edits, I read this headline: Israeli
              settlers kidnap two Palestinian children, tie them to a tree.
              <FootnoteLink url="https://www.middleeasteye.net/news/israeli-settlers-kidnap-two-palestinian-children-tie-them-tree" />{" "}
              Every choice I make to include and exclude certain content feels
              like a matter of life and death; when I remember they are already
              dead, I feel worse.
            </p>
            <p className="mb-4">
              A new film by Louis Thoreaux: <em>The Settlers</em>. In it, a clip
              of settlers discussing how they will move into Gaza, and cleanse
              it “of the camel riders.”
              <FootnoteLink url="https://x.com/I_amMukhtar/status/1916512507746832502" />
            </p>
            <p className="mb-4">
              While writing this, the Pope has died. If I had hope, I wouldn’t
              know where it came from.{" "}
            </p>
          </div>
        </div>
      </div>
    </FootnoteProvider>
  );
}

export default withBlogSEO(Easter, {
  title: "easter",
  type: "blog",
});
